---
title: "Restoring from a backup"
slug: "restoring-from-a-backup"
category: "troubleshooting"
description: "Get your library back after data loss, accidental deletion, or hardware failure — usually within minutes."
tags: ["restore", "backup", "recovery", "data loss", "disaster"]
order: 2
---

If something has gone wrong with your library data — accidental deletion, a dead hard drive, a corrupted file — a restore from backup is almost always the answer. Provided you set up [automatic backups](/help/guides/configuring-automatic-backups) on day one, you've lost at most 15 minutes of work.

## When to restore

Good candidates for "just restore":

- A pupil record or book you accidentally deleted.
- A bulk import that went wrong and you'd rather undo.
- The library database file has gone missing or won't open.
- You've moved to a new computer (see [Transferring to a new computer](/help/guides/transferring-to-a-new-computer) for the full flow).

Not usually a restore case:

- A single mistyped loan — just delete the loan from the pupil's record instead.
- A book marked lost that turned up — use **Books → Found** to undo it, without restoring the whole database.

## What a restore replaces

A restore overwrites the **entire current database** with the contents of the backup file. This means anything you've done since that backup was taken — loans, new borrowers, catalogue changes — will be **lost**.

<Callout variant="warning" title="Before you restore, take a backup">
Even if your current database is broken, it's worth trying to back it up one more time (**File → Backup now**). That way, if the restore goes wrong, you can compare and pull individual records back across.
</Callout>

## Doing the restore

<Steps>
  <Step title="Open BookBurrow">
    If the current database won't open at all, BookBurrow will show a recovery screen on startup with a **Restore from backup** button. Click it and skip to step 3.
  </Step>
  <Step title="Go to File → Restore from backup">
    You'll need the admin PIN.
  </Step>
  <Step title="Pick a backup file">
    BookBurrow scans your backup folder and lists every `.bbbackup` file with its timestamp. Sort by newest first and pick the most recent one that's from *before* whatever went wrong.
  </Step>
  <Step title="Preview the restore">
    Before anything is overwritten, BookBurrow tells you what the backup contains: number of borrowers, number of books, number of loans, and the timestamp. If these don't look right, pick a different file.
  </Step>
  <Step title="Confirm">
    BookBurrow closes, restores the file, and reopens. The restore itself usually takes a few seconds.
  </Step>
</Steps>

## Picking the right backup file

If you're not sure which backup to pick:

- **Newest is best** in most cases — you lose the least data.
- **If the problem has been happening for a while** (e.g. a silent corruption), step back through the timestamps until you find one that previews correctly.
- **If you've been testing something** (like running year-end to see what happens), pick the backup from just before you started.

The backup files are named with ISO timestamps (`bookburrow-2026-04-12-1430.bbbackup`), so you can sort them by filename and it's the same as sorting by date.

## Verifying the restore worked

After the restore finishes and BookBurrow reopens, do a quick sanity check:

1. **Borrowers tab** — expected number of pupils?
2. **Books tab** — expected number of books?
3. **Today's loans** — anything that *should* still be there (from before the backup timestamp) is present?
4. **Settings → Licence** — still activated. Restoring doesn't touch the licence so this should be fine automatically.

If something's missing that you expected to be in that backup, try an older backup file and compare.

## Restoring onto a different computer

Restores work across computers: you can take a backup file from one machine and restore it on another. This is the foundation of the [computer transfer flow](/help/guides/transferring-to-a-new-computer).

## When backups weren't running

If something has gone wrong and you don't have any backups, the options are narrower:

- **Check the default backup folder** (`Documents/BookBurrow/Backups` on Windows) in case automatic backups were on but pointing somewhere you forgot.
- **Check the Windows Recycle Bin** or your OS equivalent — the database file may have ended up there if it was accidentally deleted.
- **Check OneDrive version history** — if your database file was in a OneDrive folder, Microsoft keeps older versions for a while.
- **Contact support@bookburrow.uk** — for serious cases we can sometimes help reconstruct data from a copy of the broken database file.

And then, as soon as you're back up and running, **go and set up automatic backups** so this doesn't happen twice.
